<template>
  <div>
    <slot name="button" v-bind="{ toggleModal, label }">
      <SelectModalSharedButton
        :label="label"
        :description="selectedText"
        @toggle="toggleModal"
        :loading="loading"
      />
    </slot>
    <NamiModal v-model="showModal" large>
      <NamiModalTitle @close="toggleModal">
        {{ label }}
      </NamiModalTitle>
      <ul class="divide-y divide-neutral-200 dark:divide-neutral-700">
        <li v-for="(field, index) in fields" class="py-2">
          <h2 class="text-lg font-medium">{{ field.label }}</h2>
          <p
            class="mb-2 text-xs font-light text-neutral-600 dark:text-neutral-400"
          >
            {{
              field.type === "single"
                ? "Select one"
                : field.type === "multiple"
                  ? "Select many"
                  : "Triple-state selection; can include or exclude"
            }}
          </p>
          <SelectModalContentSingle
            v-if="field.type === 'single'"
            v-bind="field.props"
            @update:model-value="
              (v) => $emit('update', [index, 'modelValue', v])
            "
          />
          <SelectModalContentMultiple
            v-else-if="field.type === 'multiple'"
            v-bind="field.props"
            @update:model-value="
              (v) => $emit('update', [index, 'modelValue', v])
            "
          />
          <SelectModalContentTristate
            v-else-if="field.type === 'tristate'"
            v-bind="field.props"
            @update:included="(v) => $emit('update', [index, 'included', v])"
            @update:excluded="(v) => $emit('update', [index, 'excluded', v])"
            @update:included-mode="
              (v) => $emit('update', [index, 'includedMode', v])
            "
            @update:excluded-mode="
              (v) => $emit('update', [index, 'excludedMode', v])
            "
          />
        </li>
      </ul>
      <NamiButton
        button-type="secondary"
        block
        @button-click="toggleModal"
        small
        class="mt-4"
      >
        Done
      </NamiButton>
    </NamiModal>
  </div>
</template>

<script setup lang="ts">
import { type Props as SingleProps } from "./content/Single.vue";
import { type Props as MultipleProps } from "./content/Multiple.vue";
import { type Props as TristateProps } from "./content/Tristate.vue";

type SelectFieldProps<T> = {
  label: string;
  placeholder?: string;
} & (
  | ({ type: "single" } & { props: SingleProps<T> })
  | ({ type: "multiple" } & { props: MultipleProps<T> })
  | ({ type: "tristate" } & { props: TristateProps<T> })
);

const props = defineProps<{
  fields: SelectFieldProps<any>[];
  label: string;
  loading?: boolean;
}>();

defineEmits<{
  (e: "update", v: [number, string, any]): void;
}>();

const showModal = ref(false);

function toggleModal() {
  showModal.value = !showModal.value;
}

const selectedText = computed(() => {
  return props.fields
    .map((field) => {
      switch (field.type) {
        case "single": {
          return (
            field.props.options.find((s) => s.value === field.props.modelValue)
              ?.text ??
            field.placeholder ??
            "Nothing selected"
          );
        }

        case "multiple": {
          if (field.props.modelValue.length === 0) {
            return field.placeholder ?? "Nothing selected";
          }

          const firstFoundText =
            field.props.options.find(
              (o) => o.value === field.props.modelValue[0],
            )?.text ?? "???";

          if (field.props.modelValue.length === 1) {
            return firstFoundText;
          }

          return `${firstFoundText}, +${field.props.modelValue.length - 1}`;
        }

        case "tristate": {
          const myIncluded = field.props.options.filter((o) =>
            field.props.included.includes(o.value),
          );
          const myExcluded = field.props.options.filter((o) =>
            field.props.excluded.includes(o.value),
          );

          if (myIncluded.length === 0 && myExcluded.length === 0) return "Any";

          return (
            (myIncluded.length > 0 ? "Include " : "") +
            myIncluded
              .map((o) => o.text)
              .join(field.props.includedMode === "and" ? " & " : " or ") +
            (myExcluded.length > 0
              ? myIncluded.length > 0
                ? " and exclude "
                : "Exclude "
              : "") +
            myExcluded
              .map((o) => o.text)
              .join(field.props.excludedMode === "and" ? " & " : " or ")
          );
        }
      }
    })
    .map((part, index) => `${props.fields[index].label}: ${part}`)
    .join(", ");
});
</script>
